import { useState, useEffect, useRef, useMemo } from "react";
import {
  inventorySummaryApi,
  inventoryTransactionApi,
  productApi,
  productCategoryApi,
  inventoryItemDtlApi,
  vendorApi,
  stockReceiptApi
} from "../api/api";
import "./common.css";
import { useResizableColumns } from "../hooks/useResizableColumns";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../components/Pagination";
import SearchableDropdown from "../components/SearchableDropdown";
import RvDataCard, { RvDataCardList } from "../components/RvDataCard";
import BarcodeLabelPrinter from "./BarcodeLabelPrinter";

/* ══════════════════════════════════════
   TOAST
══════════════════════════════════════ */
function Toast({ message, type, onClose }) {
  useEffect(() => {
    const t = setTimeout(onClose, 3200);
    return () => clearTimeout(t);
  }, [onClose]);
  return (
    <div className={`rv-toast rv-toast--${type}`}>
      <span className="rv-toast-icon">{type === "success" ? <CheckIcon /> : <AlertIcon />}</span>
      <span className="rv-toast-msg">{message}</span>
      <button className="rv-toast-close" onClick={onClose}><CloseIcon /></button>
    </div>
  );
}

/* ══════════════════════════════════════
   STOCK LEVEL HELPER
══════════════════════════════════════ */
const getStockLevel = (qty) => {
  if (qty <= 0) return { label: "Out of Stock", cls: "rv-stock-pill--out" };
  if (qty <= 5) return { label: "Low Stock",    cls: "rv-stock-pill--low" };
  return { label: "In Stock", cls: "rv-stock-pill--in" };
};

/* ══════════════════════════════════════
   PRODUCT PIECES MODAL
   Shows all individual physical pieces & barcode tags for a selected product
══════════════════════════════════════ */
function ProductPiecesModal({
  product,
  stockEntry,
  pieces = [],
  vendors = [],
  receipts = [],
  onClose,
  onPrintTag,
  onPrintAllTags,
  onRecordAdjustment,
}) {
  const activePieces = pieces.filter(p => (Number(p.quantity) || 0) > 0);
  const soldPieces   = pieces.filter(p => (Number(p.quantity) || 0) <= 0);

  const totalActiveWeight = activePieces.reduce((acc, p) => acc + (parseFloat(p.netWeight) || 0), 0);

  return (
    <div className="rv-dialog-backdrop" onClick={onClose} style={{ zIndex: 1000 }}>
      <div
        className="rv-dialog rv-dialog--form"
        style={{
          maxWidth: "760px",
          width: "100%",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: "14px",
          background: "#fff",
          boxShadow: "0 24px 64px rgba(43, 37, 32, 0.22)",
          overflow: "hidden"
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="rv-modal-header" style={{ padding: "18px 24px", borderBottom: "1px solid rgba(185, 167, 146, .2)" }}>
          <div className="rv-modal-icon" style={{ background: "rgba(104, 26, 45, 0.1)", color: "var(--burgundy-dark)" }}>
            <BoxIcon />
          </div>
          <div style={{ flex: "1 1 auto", minWidth: 0 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--burgundy)", textTransform: "uppercase", letterSpacing: ".5px" }}>
              Vault Stock Details
            </div>
            <h3 className="rv-modal-title" style={{ margin: "2px 0 0", fontSize: 18, color: "var(--dark)", fontWeight: 700 }}>
              {product?.productName || `Product #${stockEntry?.productID}`}
            </h3>
            <div style={{ display: "flex", gap: 12, marginTop: 4, fontSize: 12.5, color: "var(--taupe)" }}>
              {product?.productCode && <span>SKU: <strong style={{ color: "var(--dark)" }}>{product.productCode}</strong></span>}
              {product?.purityStandard && <span>Purity: <strong style={{ color: "var(--dark)" }}>{product.purityStandard}</strong></span>}
            </div>
          </div>
          <button className="rv-modal-close" onClick={onClose}><CloseIcon /></button>
        </div>

        {/* Modal Body */}
        <div className="rv-modal-body" style={{ flex: "1 1 auto", overflowY: "auto", padding: "20px 24px", minHeight: 0 }}>
          {/* Quick Metrics */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10, marginBottom: 18 }}>
            <div style={{ background: "var(--pearl-bg, #fbf9f6)", border: "1px solid rgba(185,167,146,.25)", borderRadius: 8, padding: "10px 12px" }}>
              <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 600, display: "block" }}>System Stock</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: "var(--dark)", display: "block" }}>
                {(stockEntry?.quantity ?? 0).toLocaleString()} <span style={{ fontSize: 11, fontWeight: 500 }}>pcs</span>
              </span>
            </div>
            <div style={{ background: "var(--pearl-bg, #fbf9f6)", border: "1px solid rgba(185,167,146,.25)", borderRadius: 8, padding: "10px 12px" }}>
              <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 600, display: "block" }}>In Vault</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: "#2e7d32", display: "block" }}>
                {activePieces.length} <span style={{ fontSize: 11, fontWeight: 500 }}>tags</span>
              </span>
            </div>
            <div style={{ background: "var(--pearl-bg, #fbf9f6)", border: "1px solid rgba(185,167,146,.25)", borderRadius: 8, padding: "10px 12px" }}>
              <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 600, display: "block" }}>Sold / Billed</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: "var(--taupe-dark)", display: "block" }}>
                {soldPieces.length} <span style={{ fontSize: 11, fontWeight: 500 }}>tags</span>
              </span>
            </div>
            <div style={{ background: "var(--pearl-bg, #fbf9f6)", border: "1px solid rgba(185,167,146,.25)", borderRadius: 8, padding: "10px 12px" }}>
              <span style={{ fontSize: 11, color: "var(--taupe)", fontWeight: 600, display: "block" }}>Active Net Wt</span>
              <span style={{ fontSize: 17, fontWeight: 700, color: "var(--burgundy-dark)", display: "block" }}>
                {totalActiveWeight.toFixed(2)} <span style={{ fontSize: 11, fontWeight: 500 }}>g</span>
              </span>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <h4 style={{ margin: "0 0 2px 0", fontSize: 14, fontWeight: 700, color: "var(--dark)" }}>
                Physical Jewellery Pieces ({pieces.length})
              </h4>
              <p style={{ margin: 0, fontSize: 12, color: "var(--taupe)" }}>
                Vault Stock Status · Available for Billing or In-Store Adjustments
              </p>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {onRecordAdjustment && (
                <button
                  type="button"
                  className="rv-btn rv-btn--ghost"
                  style={{ padding: "6px 12px", fontSize: 11.5 }}
                  onClick={() => onRecordAdjustment(product, stockEntry)}
                >
                  ⚡ Record Adjustment / Damage
                </button>
              )}
              <p style={{ margin: 0, fontSize: 12, color: "var(--taupe)" }}>
                Each piece corresponds to a physical item in the vault with its own scannable barcode string tag.
              </p>
            </div>
            {activePieces.length > 0 && (
              <button
                type="button"
                className="rv-action-btn--print-tag"
                style={{ padding: "6px 14px", fontSize: 12 }}
                onClick={() => onPrintAllTags(activePieces, product)}
              >
                <PrintIcon /> Print All Active Tags ({activePieces.length})
              </button>
            )}
          </div>

          {pieces.length === 0 ? (
            <div style={{ textAlign: "center", padding: "36px 20px", color: "var(--taupe)", background: "rgba(185,167,146,.06)", borderRadius: 10, border: "1px dashed rgba(185,167,146,.3)" }}>
              <div style={{ width: 44, height: 44, borderRadius: "50%", background: "rgba(185,167,146,.14)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px", color: "var(--taupe-dark)" }}>
                <InvItemIcon />
              </div>
              <h4 style={{ margin: "0 0 6px 0", fontSize: 14, fontWeight: 700, color: "var(--dark)" }}>
                No Individual Barcode Tags Generated Yet
              </h4>
              <p style={{ margin: 0, fontSize: 12.5, maxWidth: 440, marginLeft: "auto", marginRight: "auto", lineHeight: 1.5 }}>
                This product currently has <strong>{(stockEntry?.quantity ?? 0)} units</strong> recorded in system stock balance without serialized piece tags. To generate unique scannable barcode string tags with per-piece gross and net weights, inward a delivery under <strong>Stock Inward</strong>.
              </p>
            </div>
          ) : (
            <div style={{ border: "1px solid rgba(185,167,146,.22)", borderRadius: 8, overflow: "hidden" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
                <thead>
                  <tr style={{ background: "rgba(185,167,146,.12)", color: "var(--taupe-dark)", textAlign: "left", borderBottom: "1px solid rgba(185,167,146,.2)" }}>
                    <th style={{ padding: "8px 10px" }}>Barcode / Tag #</th>
                    <th style={{ padding: "8px 10px" }}>Gross Wt</th>
                    <th style={{ padding: "8px 10px" }}>Less Wt</th>
                    <th style={{ padding: "8px 10px" }}>Net Wt</th>
                    <th style={{ padding: "8px 10px" }}>Status</th>
                    <th style={{ padding: "8px 10px", textAlign: "right" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {pieces.map(piece => {
                    const isSold = (Number(piece.quantity) || 0) <= 0;
                    return (
                      <tr key={piece.inventoryItemID} style={{ borderBottom: "1px solid rgba(185,167,146,.12)", background: isSold ? "rgba(0,0,0,.02)" : "#fff" }}>
                        <td style={{ padding: "8px 10px", fontWeight: 700, color: "var(--burgundy-dark)" }}>
                          #{piece.inventoryItemID}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          {piece.grossWeight ? `${parseFloat(piece.grossWeight).toFixed(3)}g` : "—"}
                        </td>
                        <td style={{ padding: "8px 10px", color: "var(--taupe)" }}>
                          {piece.lessWeight ? `${parseFloat(piece.lessWeight).toFixed(3)}g` : "0.000g"}
                        </td>
                        <td style={{ padding: "8px 10px", fontWeight: 700 }}>
                          {piece.netWeight ? `${parseFloat(piece.netWeight).toFixed(3)}g` : "—"}
                        </td>
                        <td style={{ padding: "8px 10px" }}>
                          <span style={{
                            fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 4,
                            background: !isSold ? "rgba(46,125,50,.1)" : "rgba(104,26,45,.1)",
                            color: !isSold ? "#2e7d32" : "#9c4221"
                          }}>
                            {!isSold ? "In Vault" : "Sold"}
                          </span>
                        </td>
                        <td style={{ padding: "8px 10px", textAlign: "right" }}>
                          <button
                            type="button"
                            className="rv-action-btn"
                            style={{ padding: "3px 8px", fontSize: 11.5 }}
                            title="Print this piece's barcode tag"
                            onClick={() => onPrintTag(piece, product)}
                          >
                            <PrintIcon /> Print
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="rv-modal-footer" style={{ padding: "14px 24px", borderTop: "1px solid rgba(185, 167, 146, .2)", display: "flex", justifyContent: "flex-end" }}>
          <button className="rv-btn rv-btn--ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════
   MAIN STOCK PAGE (READ-ONLY UNIFIED LEDGER)
══════════════════════════════════════ */
export default function InventorySummry() {
  const [inventory,       setInventory]       = useState([]);
  const [products,        setProducts]        = useState([]);
  const [leafCategories,  setLeafCategories]  = useState([]);
  const [items,           setItems]           = useState([]);
  const [vendors,         setVendors]         = useState([]);
  const [receipts,        setReceipts]        = useState([]);
  const [loading,         setLoading]         = useState(true);

  const [search,         setSearch]         = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterStock,    setFilterStock]    = useState("all");

  const [drawer,         setDrawer]         = useState(null);
  const [printItem,      setPrintItem]      = useState(null);
  const [printModal,     setPrintModal]     = useState(null);
  const [toast,          setToast]          = useState(null);

  /* ── Table Resizing ── */
  const tableRef = useRef(null);
  const [tableReady, setTableReady] = useState(false);
  useResizableColumns(tableRef, tableReady, [50, 220, 110, 120, 130, 110, 110]);

  /* ── Fetch All ── */
  const fetchAll = async () => {
    try {
      setLoading(true); setTableReady(false);
      const [
        invData,
        prodData,
        catData,
        itemsData,
        vendorsData,
        receiptsData
      ] = await Promise.all([
        inventorySummaryApi.getAll(),
        productApi.getAll(),
        productCategoryApi.getAll().catch(() => []),
        inventoryItemDtlApi.getAll().catch(() => []),
        vendorApi.getAll().catch(() => []),
        stockReceiptApi.getAll().catch(() => []),
      ]);

      setInventory(Array.isArray(invData) ? invData : invData?.data ?? []);
      setProducts(Array.isArray(prodData) ? prodData : prodData?.data ?? []);
      const rawCats = Array.isArray(catData) ? catData : catData?.data ?? [];
      const leafCats = rawCats.filter(c => c.parentCategoryID && c.parentCategoryID !== 0);
      setLeafCategories(leafCats);
      setItems(Array.isArray(itemsData) ? itemsData : itemsData?.data ?? []);
      setVendors(Array.isArray(vendorsData) ? vendorsData : vendorsData?.data ?? []);
      setReceipts(Array.isArray(receiptsData) ? receiptsData : receiptsData?.data ?? []);
    } catch (err) {
      showToast(err.message || "Failed to load inventory.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);
  useEffect(() => { if (!loading) setTableReady(true); }, [loading]);

  const showToast = (message, type = "success") => setToast({ message, type });

  /* ── Product Lookup & Stats ── */
  const productMap = useMemo(() => new Map(products.map(p => [p.productID, p])), [products]);

  // Group tagged pieces by productID
  const piecesByProduct = useMemo(() => {
    const map = new Map();
    items.forEach(it => {
      const pId = Number(it.productID);
      if (!map.has(pId)) map.set(pId, []);
      map.get(pId).push(it);
    });
    return map;
  }, [items]);

  /* ── Stats Strip ── */
  const totalSkus        = inventory.length;
  const totalQty         = inventory.reduce((sum, i) => sum + (i.quantity ?? 0), 0);
  const activeVaultTags  = items.filter(i => (Number(i.quantity) || 0) > 0).length;
  const outOfStock       = inventory.filter(i => (i.quantity ?? 0) === 0).length;
  const lowStock         = inventory.filter(i => (i.quantity ?? 0) > 0 && (i.quantity ?? 0) <= 5).length;

  /* ── Filtered Stock ── */
  const filteredStock = useMemo(() => {
    return inventory.filter(item => {
      const prod = productMap.get(item.productID);
      const name = (prod?.productName ?? `Product #${item.productID}`).toLowerCase();
      const code = (prod?.productCode ?? "").toLowerCase();
      const q = search.toLowerCase();

      const matchesSearch = !q || name.includes(q) || code.includes(q) || String(item.quantity).includes(q);
      const matchesCat    = filterCategory === "all" || String(prod?.categoryID) === String(filterCategory);

      const qty = item.quantity ?? 0;
      let matchesStock = true;
      if (filterStock === "instock") matchesStock = qty > 5;
      else if (filterStock === "low") matchesStock = qty > 0 && qty <= 5;
      else if (filterStock === "out") matchesStock = qty === 0;

      return matchesSearch && matchesCat && matchesStock;
    });
  }, [inventory, productMap, search, filterCategory, filterStock]);

  const {
    paged: pagedStock,
    page: stockPage,
    totalPages: stockTotalPages,
    pageSize: stockPageSize,
    setPage: setStockPage,
    setPageSize: setStockPageSize,
    pageStart: stockPageStart,
    pageEnd: stockPageEnd,
    totalItems: stockTotalItems
  } = usePagination(filteredStock, 10);

  const handleOpenDrawer = (item) => {
    const prod = productMap.get(item.productID) || { productID: item.productID, productName: `Product #${item.productID}` };
    const pPieces = piecesByProduct.get(item.productID) || [];
    setDrawer({
      product: prod,
      stockEntry: item,
      pieces: pPieces,
    });
  };

  const handlePrintSingleTag = (piece, prod) => {
    setPrintItem({
      ...piece,
      productID: prod?.productID || piece.productID,
    });
  };

  const handlePrintAllTags = (pieceList, prod) => {
    setPrintModal({
      items: pieceList,
      product: prod,
    });
  };

  return (
    <div className="rv-inv-page">
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Barcode Printers */}
      {printItem && (
        <BarcodeLabelPrinter
          item={printItem}
          product={products.find(p => p.productID === printItem.productID)}
          vendors={vendors}
          onClose={() => setPrintItem(null)}
        />
      )}
      {printModal && (
        <BarcodeLabelPrinter
          items={printModal.items}
          item={printModal.items[0]}
          product={printModal.product}
          products={products}
          vendors={vendors}
          onClose={() => setPrintModal(null)}
        />
      )}

      {/* Product Physical Pieces Modal */}
      {drawer && (
        <ProductPiecesModal
          product={drawer.product}
          stockEntry={drawer.stockEntry}
          pieces={drawer.pieces}
          vendors={vendors}
          receipts={receipts}
          onClose={() => setDrawer(null)}
          onPrintTag={handlePrintSingleTag}
          onPrintAllTags={handlePrintAllTags}
        />
      )}

      {/* ── Topbar (Stock Overview) ── */}
      <div className="rv-inv-topbar">
        <div className="rv-inv-topbar-left" style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 4 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <h2 className="rv-inv-heading">Live Vault Stock & Barcodes</h2>
            <span className="rv-inv-count">{inventory.length} products</span>
          </div>
          <p style={{ margin: 0, fontSize: "13px", color: "var(--taupe, #8a7a6b)" }}>
            Real-time store stock balance. Stock is added via <strong>Stock Inward</strong> and automatically deducted when sales invoices are finalised.
          </p>
        </div>
      </div>

      {/* ── Stats Strip ── */}
      <div className="rv-inv-stats-strip" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "12px", marginBottom: "16px" }}>
        <div className="rv-card" style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", borderRadius: "10px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(185, 167, 146, .14)", color: "var(--taupe-dark, #635343)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <BoxIcon />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--taupe, #8a7a6b)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Total Products</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--dark, #2b2520)", fontFamily: "var(--d-font)" }}>{totalSkus}</div>
          </div>
        </div>

        <div className="rv-card" style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", borderRadius: "10px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(90, 160, 100, .14)", color: "#3e7d46", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <LayersIcon />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--taupe, #8a7a6b)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Total Vault Units</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--dark, #2b2520)", fontFamily: "var(--d-font)" }}>{totalQty.toLocaleString()}</div>
          </div>
        </div>

        <div className="rv-card" style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", borderRadius: "10px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(104, 26, 45, .12)", color: "var(--burgundy-dark)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <TagIcon />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--taupe, #8a7a6b)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Tagged Vault Pieces</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "var(--burgundy-dark)", fontFamily: "var(--d-font)" }}>{activeVaultTags}</div>
          </div>
        </div>

        <div className="rv-card" style={{ padding: "14px 18px", display: "flex", alignItems: "center", gap: "12px", borderRadius: "10px" }}>
          <div style={{ width: "36px", height: "36px", borderRadius: "8px", background: "rgba(214, 158, 60, .14)", color: "#b07c20", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <WarnIcon />
          </div>
          <div>
            <div style={{ fontSize: "11px", fontWeight: 600, color: "var(--taupe, #8a7a6b)", textTransform: "uppercase", letterSpacing: "0.04em" }}>Low Stock (≤ 5)</div>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#b07c20", fontFamily: "var(--d-font)" }}>{lowStock}</div>
          </div>
        </div>
      </div>

      {/* ── Top Filters ── */}
      <div className="rv-inv-filters" style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap", marginBottom: "16px" }}>
        <div className="rv-inv-search" style={{ minWidth: "220px", flex: 1 }}>
          <SearchIcon />
          <input
            placeholder="Search by product name, code, or quantity…"
            value={search}
            onChange={e => { setSearch(e.target.value); setStockPage(1); }}
          />
          {search && (
            <button className="rv-inv-search-clear" onClick={() => { setSearch(""); setStockPage(1); }}>
              <CloseIcon />
            </button>
          )}
        </div>

        <div style={{ width: "210px", flexShrink: 0 }}>
          <SearchableDropdown
            options={[
              { id: "all", name: "All Categories" },
              ...leafCategories.map(c => ({ id: String(c.categoryID ?? c.id), name: c.categoryName ?? c.name }))
            ]}
            valueKey="id"
            labelKey="name"
            value={filterCategory}
            onChange={val => { setFilterCategory(val); setStockPage(1); }}
            placeholder="All Categories"
            searchPlaceholder="Search categories…"
            pageSize={10}
          />
        </div>

        <div className="rv-filter-tabs">
          {[
            { key: "all",     label: "All" },
            { key: "instock", label: "In Stock" },
            { key: "low",     label: "Low Stock" },
            { key: "out",     label: "Out of Stock" },
          ].map(({ key, label }) => (
            <button
              key={key}
              className={`rv-filter-tab ${filterStock === key ? "rv-filter-tab--active" : ""}`}
              onClick={() => { setFilterStock(key); setStockPage(1); }}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Stock Balance Table Card ── */}
      <div className="rv-card rv-inv-card">
        <div className="rv-desktop-table-view">
          {loading ? (
            <div className="rv-inv-empty">
              <div className="rv-loader" />
              <p>Loading stock balances…</p>
            </div>
          ) : filteredStock.length === 0 ? (
            <div className="rv-inv-empty">
              <div className="rv-empty-icon"><InvIcon /></div>
              <p className="rv-empty-title">No stock entries found</p>
              <p className="rv-empty-sub">
                {search || filterCategory !== "all" || filterStock !== "all"
                  ? "Try adjusting your filters or search terms."
                  : "Receive your first delivery in Stock Receipts to see inventory here."}
              </p>
            </div>
          ) : (
            <div className="rv-table-wrap rv-inv-table-wrap">
              <table className="rv-table rv-inv-table" ref={tableRef}>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Product</th>
                    <th>In-Stock Qty</th>
                    <th>Tagged Pieces</th>
                    <th>Total Tagged Wt</th>
                    <th>Stock Level</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pagedStock.map((item, i) => {
                    const prod = productMap.get(item.productID);
                    const prodPieces = piecesByProduct.get(item.productID) || [];
                    const activeP = prodPieces.filter(p => (Number(p.quantity) || 0) > 0);
                    const activeWt = activeP.reduce((acc, p) => acc + (parseFloat(p.netWeight) || 0), 0);
                    const stock = getStockLevel(item.quantity ?? 0);

                    return (
                      <tr
                        key={item.stockID}
                        className="rv-inv-row"
                        onClick={() => handleOpenDrawer(item)}
                        style={{ cursor: "pointer" }}
                      >
                        <td className="rv-inv-idx">{stockPageStart + i}</td>
                        <td>
                          <div className="rv-inv-product-cell">
                            <span className="rv-inv-product-name">
                              {prod?.productName ?? `Product #${item.productID}`}
                            </span>
                            {prod?.productCode && (
                              <span style={{ fontSize: 11, color: "var(--taupe)" }}>{prod.productCode}</span>
                            )}
                          </div>
                        </td>
                        <td>
                          <span className="rv-inv-qty">{(item.quantity ?? 0).toLocaleString()} pcs</span>
                        </td>
                        <td>
                          <span style={{
                            fontSize: 12.5, fontWeight: 600,
                            color: activeP.length > 0 ? "var(--dark)" : "var(--taupe)"
                          }}>
                            {activeP.length} {activeP.length === 1 ? "piece" : "pieces"}
                          </span>
                        </td>
                        <td>
                          <span style={{ fontSize: 12.5, fontWeight: 700, color: "var(--burgundy-dark)" }}>
                            {activeWt > 0 ? `${activeWt.toFixed(3)} g` : "—"}
                          </span>
                        </td>
                        <td>
                          <span className={`rv-stock-pill ${stock.cls}`}>
                            <span className="rv-status-dot" />
                            {stock.label}
                          </span>
                        </td>
                        <td onClick={e => e.stopPropagation()}>
                          <div className="rv-actions">
                            <button
                              type="button"
                              className="rv-btn rv-btn--ghost"
                              style={{ padding: "4px 10px", fontSize: 12 }}
                              title="View individual pieces & reprint barcode tags"
                              onClick={() => handleOpenDrawer(item)}
                            >
                              <TagIcon /> View Pieces
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Mobile View */}
        <div className="rv-mobile-cards-view">
          <RvDataCardList
            loading={loading}
            items={pagedStock}
            emptyIcon={<InvIcon />}
            emptyTitle="No stock entries found"
            emptySubtitle={search ? "Try a different search term." : "Receive your first delivery in Stock Receipts to see inventory here."}
            renderCard={(item, i) => {
              const prod = productMap.get(item.productID);
              const prodPieces = piecesByProduct.get(item.productID) || [];
              const activeP = prodPieces.filter(p => (Number(p.quantity) || 0) > 0);
              const activeWt = activeP.reduce((acc, p) => acc + (parseFloat(p.netWeight) || 0), 0);
              const stock = getStockLevel(item.quantity ?? 0);

              return (
                <RvDataCard
                  key={item.stockID}
                  index={stockPageStart + i}
                  title={prod?.productName ?? `Product #${item.productID}`}
                  subtitle={`In Vault: ${(item.quantity ?? 0).toLocaleString()} pcs · ${activeP.length} tags`}
                  badge={
                    <span className={`rv-stock-pill ${stock.cls}`}>
                      <span className="rv-status-dot" />
                      {stock.label}
                    </span>
                  }
                  fields={[
                    { label: "Quantity", value: `${(item.quantity ?? 0).toLocaleString()} pcs` },
                    { label: "Tagged Pieces", value: `${activeP.length} pieces` },
                    { label: "Total Net Wt", value: activeWt > 0 ? `${activeWt.toFixed(3)} g` : "—" },
                  ]}
                  onClick={() => handleOpenDrawer(item)}
                  actions={
                    <div className="rv-actions" onClick={e => e.stopPropagation()}>
                      <button
                        type="button"
                        className="rv-btn rv-btn--ghost"
                        style={{ padding: "4px 10px", fontSize: 12 }}
                        onClick={() => handleOpenDrawer(item)}
                      >
                        <TagIcon /> View Pieces
                      </button>
                    </div>
                  }
                />
              );
            }}
          />
        </div>

        {!loading && filteredStock.length > 0 && (
          <Pagination
            page={stockPage}
            totalPages={stockTotalPages}
            pageSize={stockPageSize}
            setPage={setStockPage}
            setPageSize={setStockPageSize}
            pageStart={stockPageStart}
            pageEnd={stockPageEnd}
            totalItems={stockTotalItems}
          />
        )}
      </div>

    </div>
  );
}

/* ── Icons ── */
function PlusIcon()         { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>; }
function SearchIcon()       { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>; }
function CloseIcon()        { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>; }
function CheckIcon()        { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>; }
function AlertIcon()        { return <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>; }
function InvIcon()          { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M5 8h14M5 8a2 2 0 1 1-4 0 2 2 0 0 1 4 0zM5 8v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8m-9 4h4"/><path d="M9 3h6l2 5H7L9 3z"/></svg>; }
function BoxIcon()          { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function LayersIcon()       { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polygon points="12 2 2 7 12 12 22 7 12 2"/><polyline points="2 17 12 22 22 17"/><polyline points="2 12 12 17 22 12"/></svg>; }
function WarnIcon()         { return <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>; }
function TagIcon()          { return <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>; }
function InvItemIcon()      { return <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/></svg>; }
function PrintIcon()        { return <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><polyline points="6 9 6 2 18 2 18 9"/><path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/><rect x="6" y="14" width="12" height="8"/></svg>; }
